import { memo, useMemo, useCallback } from "react"
import { cn } from "@shared/lib/utils"
import { type StatusBadgeProps, statusVariants } from "./variants"

const StatusBadge = memo<StatusBadgeProps>(
  ({
    status,
    statusConfig,
    defaultStatus = "unknown",
    size,
    onClick,
    "data-testid": testId,
  }) => {
    const config = useMemo(() => {
      const normalizedStatus =
        status?.toLowerCase()?.replace(/\s+/g, "_") || defaultStatus
      return (
        statusConfig[normalizedStatus] ||
        statusConfig[defaultStatus] || {
          text: status || "Unknown",
          variant: "neutral" as const,
          tooltip: `Status: ${status || "Unknown"}`,
        }
      )
    }, [status, statusConfig, defaultStatus])

    const handleClick = useCallback(() => {
      if (onClick && config.actionable) {
        onClick(status)
      }
    }, [onClick, status, config.actionable])

    return (
      <span
        className={cn(
          statusVariants({ variant: config.variant, size }),
          onClick && config.actionable && "cursor-pointer hover:shadow-sm",
        )}
        onClick={handleClick}
        role={onClick && config.actionable ? "button" : undefined}
        tabIndex={onClick && config.actionable ? 0 : undefined}
        aria-label={config.tooltip}
        data-testid={
          testId || `status-badge-${status?.toLowerCase()?.replace(/\s+/g, "-")}`
        }
      >
        {config.text}
      </span>
    )
  },
)
StatusBadge.displayName = "StatusBadge"

export { StatusBadge } 
